import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";
import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/cn.js";
const ToastContext = React.createContext(null);
const TONE_ICON = {
    success: CheckCircle2,
    error: XCircle,
    warning: AlertTriangle,
    info: Info,
};
const TONE_CLASSES = {
    success: "border-status-success/40 text-status-success",
    error: "border-status-critical/40 text-status-critical",
    warning: "border-status-warning/40 text-status-warning",
    info: "border-line text-fg-secondary",
};
let idCounter = 0;
/**
 * A transient, non-blocking notification queue — the shared alternative
 * to the ad hoc inline "action error" banners every feature page has
 * been hand-rolling its own copy of. `role="status"`/`aria-live="polite"`
 * per §11 accessibility conventions; never used for anything that must
 * block interaction (that's `Dialog`'s job) or anything Live Mode-critical
 * (§8.13 forbids any transient, easy-to-miss surface for that).
 */
export function ToastProvider({ children }) {
    const [toasts, setToasts] = React.useState([]);
    const timers = React.useRef(new Map());
    const dismiss = React.useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
        const timer = timers.current.get(id);
        if (timer) {
            clearTimeout(timer);
            timers.current.delete(id);
        }
    }, []);
    const toast = React.useCallback((input) => {
        const id = `toast-${(idCounter += 1)}`;
        const durationMs = input.durationMs ?? 5000;
        const record = {
            id,
            tone: input.tone ?? "info",
            title: input.title,
            description: input.description,
            durationMs,
        };
        setToasts((prev) => [...prev, record]);
        if (durationMs > 0) {
            timers.current.set(id, setTimeout(() => dismiss(id), durationMs));
        }
        return id;
    }, [dismiss]);
    React.useEffect(() => {
        const activeTimers = timers.current;
        return () => activeTimers.forEach((timer) => clearTimeout(timer));
    }, []);
    const value = React.useMemo(() => ({ toast, dismiss }), [toast, dismiss]);
    return (_jsxs(ToastContext.Provider, { value: value, children: [children, typeof document !== "undefined"
                ? createPortal(_jsx(ToastViewport, { toasts: toasts, onDismiss: dismiss }), document.body)
                : null] }));
}
function ToastViewport({ toasts, onDismiss, }) {
    return (_jsx("div", { role: "status", "aria-live": "polite", className: "pointer-events-none fixed bottom-4 right-4 z-toast flex w-96 max-w-[calc(100vw-2rem)] flex-col gap-2", children: toasts.map((t) => {
            const Icon = TONE_ICON[t.tone];
            return (_jsxs("div", { className: cn("pointer-events-auto flex items-start gap-2.5 rounded-md border bg-surface-overlay px-3 py-2.5 shadow-lg", "motion-safe:animate-in motion-safe:slide-in-from-bottom-2 motion-safe:duration-base", TONE_CLASSES[t.tone]), children: [_jsx(Icon, { className: "mt-0.5 size-4 shrink-0" }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("p", { className: "text-body text-fg-primary", children: t.title }), t.description ? (_jsx("p", { className: "text-caption text-fg-tertiary", children: t.description })) : null] }), _jsx("button", { type: "button", "aria-label": "Dismiss notification", onClick: () => onDismiss(t.id), className: "shrink-0 text-fg-tertiary hover:text-fg-primary", children: _jsx(X, { className: "size-4" }) })] }, t.id));
        }) }));
}
/** Throws outside `ToastProvider` — every screen that can call this must already be mounted under it (the app shell mounts it once, at the root). */
export function useToast() {
    const ctx = React.useContext(ToastContext);
    if (!ctx)
        throw new Error("useToast must be used within a <ToastProvider>");
    return ctx;
}
//# sourceMappingURL=toast.js.map